import { money } from './units';
import nacl from 'tweetnacl';

export const APP_INFO = {
  name: 'GISS FieldRapport Pro',
  publisher: 'GISS - Software Solutions',
  web: 'www.giss.cc',
  packageId: 'cc.giss.fieldrapport',
  version: '1.5.1',
  backupExt: '.giss',
};

export const MODULE_PACKS = { BASIS: ['pro'], INV: ['inventory'], ALL: ['pro', 'inventory'] };
export const PACK_PRICE = { BASIS: 18, INV: 18, ALL: 18 };
export const GEN_PACKS = ['ALL'];
export const DURATIONS = [365, 730];
export const DEMO_DAYS = 14;

const API = `${process.env.REACT_APP_BACKEND_URL}/api/license`;
let PUB = null;
const pubKey = () => {
  if (!PUB) PUB = Uint8Array.from(atob(String(process.env.REACT_APP_LICENSE_PUBLIC_KEY || '').trim()), (c) => c.charCodeAt(0));
  return PUB;
};
const TOK_KEY = 'giss_license_tokens';
const DEV_KEY = 'giss_device_id';
const OWNER_KEY = 'giss_owner_token';
const LOGO_KEY = 'giss_company_logo';
const PRICE_KEY = 'giss_material_prices';
const FIRST_KEY = 'giss_first_run';
const HAD_KEY = 'giss_had_license';

export function getDeviceId() {
  let id = localStorage.getItem(DEV_KEY);
  if (!/^GISS-DEV-\d{6}$/.test(id || '')) {
    const rnd = crypto.getRandomValues(new Uint32Array(1))[0] % 1000000;
    id = `GISS-DEV-${String(rnd).padStart(6, '0')}`;
    localStorage.setItem(DEV_KEY, id);
  }
  return id;
}

const b64u = (s) => Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4)), (c) => c.charCodeAt(0));

function verifyToken(tok) {
  try {
    const [body, sig] = tok.split('.');
    const bytes = b64u(body);
    if (!nacl.sign.detached.verify(bytes, b64u(sig), pubKey())) return null;
    const p = JSON.parse(new TextDecoder().decode(bytes));
    return p.device === getDeviceId() ? p : null;
  } catch { return null; }
}

const tokens = () => { try { return JSON.parse(localStorage.getItem(TOK_KEY)) || []; } catch { return []; } };

export const getLicenses = () => tokens().map(verifyToken).filter(Boolean)
  .map((p) => ({ ...p, expired: p.exp != null && p.exp < Date.now() }));
// raw token of the first valid, not expired licence (sent to the server for agent credit)
export const activeLicenseToken = () => tokens().find((tk) => { const p = verifyToken(tk); return p && !(p.exp != null && p.exp < Date.now()); }) || '';

export function demoInfo() {
  let first = +localStorage.getItem(FIRST_KEY);
  if (!first) { first = Date.now(); localStorage.setItem(FIRST_KEY, String(first)); }
  const end = first + DEMO_DAYS * 864e5;
  const used = !!localStorage.getItem(HAD_KEY) || getLicenses().length > 0;
  return { used, end, active: !used && Date.now() < end, daysLeft: Math.max(0, Math.ceil((end - Date.now()) / 864e5)) };
}

export const getModules = () => {
  // v1.2.1: one complete licence – any valid licence (also legacy BASIS / INV) unlocks everything
  return getLicenses().some((l) => !l.expired) || demoInfo().active ? ['pro', 'inventory'] : [];
};
export const daysLeft = (exp) => Math.max(0, Math.ceil((exp - Date.now()) / 864e5));
export const hasModule = (m) => getModules().includes(m);

export function isAdmin() {
  const [dev, exp] = (localStorage.getItem(OWNER_KEY) || '').split('|');
  return dev === getDeviceId() && +exp * 1000 > Date.now();
}
export const leaveOwner = () => {
  localStorage.removeItem(OWNER_KEY);
  localStorage.setItem(TOK_KEY, JSON.stringify(tokens().filter((tk) => verifyToken(tk)?.code !== 'OWNER')));
};

export function storeToken(token) {
  const p = verifyToken(token);
  if (!p) return null;
  const rest = tokens().filter((tk) => verifyToken(tk)?.code !== p.code);
  localStorage.setItem(TOK_KEY, JSON.stringify([...rest, token]));
  localStorage.setItem(HAD_KEY, '1');
  window.dispatchEvent(new Event('giss-policy'));
  return p;
}
export const enterpriseToken = () => tokens().find((tk) => String(verifyToken(tk)?.code || '').startsWith('GISS-ENT-')) || '';
export const enterpriseLicense = () => getLicenses().find((l) => !l.expired && String(l.code).startsWith('GISS-ENT-')) || null;

// returns { ok, mods, admin, exp } or { error: 'invalid'|'device'|'expired'|'revoked'|'offline' }
export async function activateCode(input) {
  let res;
  try {
    res = await fetch(`${API}/activate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ device_id: getDeviceId(), code: String(input || '').trim() }) });
  } catch { return { error: 'offline' }; }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { error: data.detail || 'invalid' };
  const p = storeToken(data.token);
  if (!p) return { error: 'invalid' };
  if (data.owner_token) localStorage.setItem(OWNER_KEY, data.owner_token);
  return { ok: true, mods: p.mods, exp: p.exp, admin: !!data.owner_token };
}

async function ownerCall(path, opts = {}) {
  const res = await fetch(opts.url || `${API}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', 'X-Owner-Token': localStorage.getItem(OWNER_KEY) || '', ...(opts.headers || {}) } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || 'error');
  return data;
}
export const generateCode = (device_id, pack, days, note) => ownerCall('/generate', { method: 'POST', body: JSON.stringify({ device_id, pack, days, note }) });
export const listCodes = () => ownerCall('/codes');
export const funnelStats = () => ownerCall('/funnel');
export const generateEnterprise = (company, seats, days, note) => ownerCall('/enterprise', { method: 'POST', body: JSON.stringify({ company, seats, days, note }) });
export const extendEnterprise = (code, days = 365) => ownerCall(`/enterprise/${encodeURIComponent(code)}/extend`, { method: 'POST', body: JSON.stringify({ days }) });
export const releaseDevice = (code, device_id) => ownerCall(`/enterprise/${encodeURIComponent(code)}/release`, { method: 'POST', body: JSON.stringify({ device_id }) });
export const revokeCode = (code) => ownerCall(`/revoke/${encodeURIComponent(code)}`, { method: 'POST' });
export const resetKritis = (code) => ownerCall('', { url: `${process.env.REACT_APP_BACKEND_URL}/api/kritis/reset/${encodeURIComponent(code)}`, method: 'POST' });
const ADMIN = `${process.env.REACT_APP_BACKEND_URL}/api/admin/play`;
const adminCall = (path, body) => ownerCall('', { url: `${ADMIN}${path}`, ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}) });
export const playAdminStatus = () => adminCall('');
export const uploadPlayKey = (json_text) => adminCall('/key', { json_text });
export const setPlayMode = (mode) => adminCall('/mode', { mode });
export const uploadKeystore = (zip_base64, password) => adminCall('/keystore', { zip_base64, password });
export const playBindings = () => adminCall('/bindings');
export const resetPlayBinding = (id) => adminCall(`/bindings/${id}/reset`, {});

const CONTACT_KEY = 'giss_vendor_contact';
const BLOCKED_KEY = 'giss_license_blocked';
const readJson = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) || d; } catch { return d; } };
export const getContact = () => ({ name: APP_INFO.publisher, phone: '', email: '', web: APP_INFO.web, ...Object.fromEntries(Object.entries(readJson(CONTACT_KEY, {})).filter(([, v]) => v)) });
export const getBlocked = () => readJson(BLOCKED_KEY, []);
export const clearBlocked = () => localStorage.removeItem(BLOCKED_KEY);
export const saveContact = async (c) => { const d = await ownerCall('/contact', { method: 'PUT', body: JSON.stringify(c) }); localStorage.setItem(CONTACT_KEY, JSON.stringify(d)); return d; };

// server re-check: removes revoked/expired/foreign codes; returns list of newly blocked licences
export async function syncLicenses() {
  if (!navigator.onLine) return [];
  const lics = getLicenses().filter((l) => l.code !== 'OWNER');
  const ownTok = tokens().find((tk) => verifyToken(tk)?.code === 'OWNER');
  let data;
  try {
    const res = await fetch(`${API}/check`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ device_id: getDeviceId(), codes: lics.map((l) => l.code), ...(ownTok ? { owner_tok: ownTok } : {}) }) });
    if (!res.ok) return [];
    data = await res.json();
  } catch { return []; }
  if (data.contact) localStorage.setItem(CONTACT_KEY, JSON.stringify(data.contact));
  if (ownTok && data.statuses?.OWNER === 'revoked') leaveOwner();
  Object.values(data.tokens || {}).forEach(storeToken);
  const bad = lics.filter((l) => data.statuses?.[l.code] && !['active', 'expired'].includes(data.statuses[l.code]))
    .map((l) => ({ code: l.code, mods: l.mods, reason: data.statuses[l.code] }));
  if (!bad.length) return [];
  const codes = bad.map((b) => b.code);
  localStorage.setItem(TOK_KEY, JSON.stringify(tokens().filter((tk) => !codes.includes(verifyToken(tk)?.code))));
  localStorage.setItem(BLOCKED_KEY, JSON.stringify([...getBlocked().filter((b) => !codes.includes(b.code)), ...bad]));
  return bad;
}

const DEMO_TOK = 'giss_demo_token';
// signed token proving the AI counter is unlocked (licence with inventory module or server-side demo)
export const aiToken = () => signedToken((p) => p.mods.includes('inventory') || p.mods.includes('pro'));
// any valid GISS licence or server-side demo (e.g. DWG conversion on the server)
export const licToken = () => signedToken(() => true);

async function signedToken(need) {
  if (enterpriseLicense()?.pol?.kritis) { window.dispatchEvent(new Event('giss-kritis-block')); throw new Error('kritis'); }
  const ok = (p) => p && need(p) && (p.exp == null || p.exp > Date.now());
  const lic = tokens().find((tk) => ok(verifyToken(tk)));
  if (lic) return lic;
  if (!demoInfo().active) return null;
  const cached = localStorage.getItem(DEMO_TOK);
  if (cached && ok(verifyToken(cached))) return cached;
  try {
    const res = await fetch(`${API}/demo`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ device_id: getDeviceId() }) });
    if (!res.ok) return null;
    const { token } = await res.json();
    localStorage.setItem(DEMO_TOK, token);
    return token;
  } catch { return null; }
}

export const fill = (s, v) => s.replace(/\{(\w+)\}/g, (_, k) => v[k] ?? '');

export const expiringLicenses = (days = 7) => {
  const all = getLicenses();
  const soon = (l) => l.exp != null && l.exp - Date.now() < days * 864e5;
  const covered = new Set(all.filter((l) => !soon(l)).flatMap((l) => l.mods));
  return all.filter((l) => soon(l) && !l.mods.every((m) => covered.has(m)));
};

export const getLogo = () => localStorage.getItem(LOGO_KEY);
export const setLogo = (data) => (data ? localStorage.setItem(LOGO_KEY, data) : localStorage.removeItem(LOGO_KEY));

export const getPrices = () => { try { return JSON.parse(localStorage.getItem(PRICE_KEY)) || {}; } catch { return {}; } };
export const setPrice = (name, price) => {
  const p = getPrices();
  const v = parsePrice(price);
  if (v == null) delete p[name]; else p[name] = v;
  localStorage.setItem(PRICE_KEY, JSON.stringify(p));
  return p;
};
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9äöüß]/g, '').replace(/(en|n|e|s|i)$/, '');
// exact label first, then tolerant match (case, spaces, plural endings, "Schraube M8" vs "Schrauben")
export const findPrice = (prices, label) => {
  if (prices[label] != null) return prices[label];
  const n = norm(label);
  const keys = Object.keys(prices);
  const k = keys.find((x) => norm(x) === n)
    || keys.find((x) => { const m = norm(x); return m.length >= 4 && n.length >= 4 && (m.includes(n) || n.includes(m)); })
    || keys.map((x) => [x, near(norm(x), n)]).filter(([, d]) => n.length >= 4 && d <= (n.length >= 7 ? 2 : 1)).sort((a, b) => a[1] - b[1])[0]?.[0];
  return k != null ? prices[k] : undefined;
};
const near = (m, n) => Math.min(...[-1, 0, 1].map((o) => lev(m.slice(0, n.length + o), n)));
// edit distance for typos like "Schrube" vs "Schraube"
function lev(a, b) {
  const d = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let prev = d[0]; d[0] = i;
    for (let j = 1; j <= b.length; j++) { const tmp = d[j]; d[j] = Math.min(d[j] + 1, d[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1)); prev = tmp; }
  }
  return d[b.length];
}
export const parsePrice = (v) => { const s = String(v ?? '').trim().replace(',', '.'); return s === '' || Number.isNaN(+s) ? null : +s; };
export const eur = (n) => money(n || 0);

// Google Play links (admin-editable via contact settings) + tester signup
export const playLinks = () => {
  const c = getContact();
  const store = c.play_url || `https://play.google.com/store/apps/details?id=${APP_INFO.packageId}`;
  return { store, test: c.play_test_url || '', open: c.play_test_url || store };
};
export async function refreshContact() {
  try {
    const r = await fetch(`${API}/contact`);
    if (r.ok) localStorage.setItem(CONTACT_KEY, JSON.stringify(await r.json()));
  } catch { /* offline */ }
  return getContact();
}
export async function signupTester(email, lang) {
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/testers`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, lang, device_id: getDeviceId() }) });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'error');
}
export const listTesters = () => ownerCall('/testers');

// PC unlock code: paid (non-demo, non-PC) licence token of this device
export const paidToken = () => tokens().find((tk) => { const p = verifyToken(tk); return p && p.code !== 'DEMO' && !String(p.code).startsWith('GISS-PC-') && (p.exp == null || p.exp > Date.now()); }) || null;
export const pcLicense = () => getLicenses().find((l) => String(l.code).startsWith('GISS-PC-')) || null;
async function pcPost(path, body) {
  const r = await fetch(`${API}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ device_id: getDeviceId(), ...body }) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || 'error');
  return d;
}
export const pcCodeGet = (email = '') => pcPost('/pc-code', { token: paidToken(), email });
export const pcCodeRelease = () => pcPost('/pc-release', { token: paidToken() });
export async function pcActivate(email, code) {
  const d = await pcPost('/pc-activate', { email, code });
  return storeToken(d.token);
}
